import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUserById, updateUser } from "../services/userService";
import Loader from "../child/Loder";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { handleUnauthorized } from "../../utils/apiHelper";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserById, updateUserById } from "../../redux/user/userThunks";
import { clearUserData } from "../../redux/user/userSlice";

const EditUserPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const addressInputRef = useRef(null);

  const { userData, loading, error } = useSelector((state) => state.user);

  const [formData, setFormData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [coordinates, setCoordinates] = useState([]);

  useEffect(() => {
    const existingScript = document.querySelector(
      "script[src*='maps.googleapis.com']"
    );

    if (existingScript) return;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;

    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.google?.maps?.places && addressInputRef.current) {
        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          { types: ["geocode"] }
        );

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();

          if (!place.geometry) return;

          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          console.log("Selected place:", place.formatted_address, lat, lng);

          setFormData((prev) => ({
            ...prev,
            location: {
              type: "Point",
              coordinates: [lng, lat],
              address: place.formatted_address,
            },
          }));

          setCoordinates([lng, lat]);
          setErrorMessage("");
        });

        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Fetch user data on mount
  useEffect(() => {
    dispatch(fetchUserById({ id, navigate }));
    return () => dispatch(clearUserData());
  }, [dispatch, id, navigate]);

  // Sync redux userData to local formData
  // useEffect(() => {
  //   if (userData) setFormData(userData);
  // }, [userData]);

  useEffect(() => {
  if (userData && !formData) {
    setFormData(userData);
  }
}, [userData]);


  const validateName = (name) => {
    const trimmed = name.trim();
    const regex = /^[a-zA-Z\s'-]{2,50}$/; // letters, spaces, apostrophes, hyphens

    if (!trimmed) return "Name is required";
    if (!regex.test(trimmed))
      return "Name can only contain letters, spaces, hyphens, and apostrophes";
    return "";
  };
  const validateAge = (age) => {
    if (age === "" || age === null || age === undefined)
      return "Age is required";

    const num = Number(age);
    if (isNaN(num)) return "Age must be a number";
    if (num < 1) return "Age must be at least 1";
    if (num > 120) return "Age must be less than or equal to 120";

    return "";
  };

  const validateHeight = (height) => {
    if (height === "" || height === null || height === undefined)
      return "Height is required";

    const num = Number(height);
    if (isNaN(num)) return "Height must be a number";
    return "";
  };
  // Handle form change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (!formData) return;

    if (name.startsWith("location.")) {
      const locField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          [locField]: value,
        },
      }));

      if (name === "fullName") {
        const error = validateName(value);
        setErrors((prev) => ({ ...prev, fullName: error }));
      }

      if (name === "age") {
        const error = validateAge(value);
        setErrors((prev) => ({ ...prev, age: error }));
      }
      if (name === "height") {
        const error = validateHeight(value);
        setErrors((prev) => ({ ...prev, height: error }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData) return;
    setErrorMessage("");

    // A member with no saved location (founder 2026-10-07: Save on
    // jordan.demo "does nothing") must get an address picked first. This
    // message used to be set but never shown.
    if (!formData.location?.coordinates?.length) {
      setErrorMessage(
        "This member has no saved location. Type a city in Address, pick it from the suggestions, then save."
      );
      return;
    }

    const resultAction = await dispatch(
      updateUserById({ id, data: formData, navigate })
    );

    if (updateUserById.fulfilled.match(resultAction)) {
      setSuccessMessage("User updated successfully");
      setTimeout(() => {
        setSuccessMessage("");
        navigate("/user");
      }, 500);
    }
  };

  if (loading || !formData) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            {successMessage && <SuccessAlert message={successMessage} />}
            {error && <DengerAlert message={error} />}

            <form onSubmit={handleSubmit} className="row gy-3">
              <div className="col-md-6">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  name="fullName"
                  className="form-control"
                  placeholder="Enter Name"
                  value={formData.fullName || ""}
                  onChange={handleChange}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                      .trimStart(); // Prevent leading spaces
                  }}
                />
                {errors.fullName && (
                  <div className="text-danger mt-1">{errors.fullName}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formData.email || ""}
                  // onChange={handleChange}
                  disabled
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Age</label>
                <input
                  type="number"
                  name="age"
                  min="14"
                  className="form-control"
                  value={formData.age || ""}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (["-", "e", "E"].includes(e.key)) {
                      e.preventDefault(); // block negative and exponential input
                    }
                  }}
                  onWheel={(e) => e.target.blur()} // optional: prevent scroll from changing number
                />
                {errors.age && (
                  <div className="text-danger mt-1">{errors.age}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">Height (ft)</label>
                <input
                  type="number"
                  name="height"
                  step="0.1" // allow decimal input like 5.5
                  className="form-control"
                  value={formData.height || ""}
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()} // optional
                  onKeyDown={(e) => {
                    if (["-", "e", "E"].includes(e.key)) {
                      e.preventDefault(); // block negative and exponential input
                    }
                  }}
                />
                {errors.height && (
                  <div className="text-danger mt-1">{errors.height}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">gender</label>
                <select
                  name="gender"
                  className="form-select"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  <option value="man">Man</option>
                  <option value="woman">Woman</option>
                  <option value="beyond_binary">Beyond Binary</option>
                </select>
              </div>

              {/* <div className="col-md-3">
                <label className="form-label">Email Verified</label>
                <select
                  name="isEmailVerified"
                  className="form-select"
                  value={formData.isEmailVerified ? "true" : "false"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isEmailVerified: e.target.value === "true",
                    }))
                  }
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div> */}

              {/* Test / quarantine account (founder ask 2026-09-01): a
                  test-flagged member uses the whole app normally but the
                  matcher never deals them, nudges skip them and counts
                  exclude them. Also the fix-it quarantine for accounts in
                  repair (real photos, real name) before re-entering the
                  pool. Was DB-only until now. */}
              <div className="col-md-6">
                <label className="form-label">Test account (invisible to matching)</label>
                <select
                  name="isTestAccount"
                  className="form-select"
                  value={formData.isTestAccount ? "true" : "false"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isTestAccount: e.target.value === "true",
                    }))
                  }
                >
                  <option value="false">No - real member</option>
                  <option value="true">Yes - never dealt, excluded from counts</option>
                </select>
                <small className="text-muted">
                  Use for QA, observers, and members in repair. They can use
                  the app fully but no real member is ever introduced to them.
                </small>
              </div>

              <div className="col-md-6">
                <label className="form-label">Login Type</label>
                <input
                  type="text"
                  name="loginType"
                  className="form-control"
                  value={formData.loginType || ""}
                  disabled
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Address</label>
                {/* <input
                  type="text"
                  name="location.address"
                  className="form-control"
                  placeholder="Enter Address"
                  value={formData.location?.address || ""}
                  onChange={handleChange}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/\s{2,}/g, " ")
                      .trimStart();
                  }}
                /> */}

                <input
                  ref={addressInputRef}
                  type="text"
                  name="location.address"
                  className="form-control"
                  placeholder="Enter Address"
                  value={formData.location?.address || ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    setFormData((prev) => ({
                      ...prev,
                      location: {
                        ...prev.location,
                        address: value,
                      },
                    }));
                  }}
                />
                {errorMessage && (
                  <div className="text-danger mt-1">{errorMessage}</div>
                )}
              </div>

              <div className="col-12 text-end">
                <button className="btn btn-primary" type="submit">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserPage;
